/**
 * Guards modal <dialog> drawers from closing when a NATIVE file picker opened
 * from inside the drawer is dismissed.
 *
 * A native file chooser isn't part of the browser's dialog top-layer stack, so
 * its Escape/Cancel leaks to the underlying <dialog> and fires its `cancel`
 * (and a stray backdrop interaction), closing the whole drawer. FileDropzone
 * marks the guard active when it opens a picker; Drawer ignores auto-close while
 * the guard is active (and briefly after focus returns, since the close fires then).
 */
let active = false;
let clearTimer: ReturnType<typeof setTimeout> | undefined;
let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
let onFocus: (() => void) | undefined;

/** Tear down any in-flight guard state (listener + timers) so overlapping
 *  beginFileDialog() calls never stack listeners or leave the flag stuck on. */
function reset(): void {
  active = false;
  if (onFocus) window.removeEventListener('focus', onFocus);
  if (clearTimer) clearTimeout(clearTimer);
  if (fallbackTimer) clearTimeout(fallbackTimer);
  onFocus = clearTimer = fallbackTimer = undefined;
}

export function beginFileDialog(): void {
  reset();
  active = true;
  onFocus = () => {
    if (onFocus) window.removeEventListener('focus', onFocus);
    onFocus = undefined;
    if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = undefined; }
    // Hold the guard a beat past focus-return so the dismissal's close is ignored.
    if (clearTimer) clearTimeout(clearTimer);
    clearTimer = setTimeout(() => { active = false; }, 400);
  };
  window.addEventListener('focus', onFocus);
  // Safety net: never leave the guard stuck active if focus never returns.
  fallbackTimer = setTimeout(reset, 60_000);
}

export function isFileDialogGuardActive(): boolean {
  return active;
}
